/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {useCallback, useEffect, useMemo, useRef} from 'react';
import * as reactiveUtils from '@arcgis/core/core/reactiveUtils';
import {useHasMounted} from '@ion/core/src/hooks/useHasMounted';
import {safeJSONStringify} from '@ion/core/src/utils/JSONUtils';
import {
  BACKGROUND_NODE_LAYER,
  BACKGROUND_PATH_LAYER,
  BACKGROUND_WORLD_LAYER,
  END_EVENT_NODE_LAYER,
  IN_TRIP_NODE_LAYER,
  IN_TRIP_PATH_LAYER,
  mapViewGoToOptions
} from '@ion/dls/src/maps/constants';
import {removeLayers} from '@ion/dls/src/maps/helpers/MapViewUtils';
import AssetHistoryLayerV2 from '@ion/dls/src/maps/layers/AssetHistoryLayerV2';
import {HISTORY_LAYER} from '@ion/dls/src/maps/layers/TripHistoryLayerSettings';
import {MapContainerProps} from '@ion/dls/src/maps/types/types';
import {setStreetViewAsset} from '@/components/streetview/store';
import useAssetHistoryProvider from '../hooks/useAssetHistoryProvider';

/*
 * AssetHistoryLayerContainer
 *
 * Container component responsible for orchestrating the Asset History map layer lifecycle.
 *
 * Key responsibilities:
 * - Initializes and passes map/view context to AssetHistoryLayerV2
 * - Cleans up previously added history-related layers on mount and query changes
 * - Handles initial map extent navigation once the view is ready
 * - Bridges UI/interaction state (hover, selection, timeline) with map layer rendering
 * - Integrates asset history provider state (vision, preferences, snap-to-road, filters)
 * - Ensures safe execution using mounted ref to avoid side effects after unmount
 *
 * Layer management:
 * - Removes IN_TRIP, BACKGROUND, and END_EVENT layers when needed to avoid duplication
 *
 * Side effects:
 * - Syncs external view via onSetView
 * - Reacts to queryParams changes to reset map layers
 * - Applies initialExtent with delayed goTo after view readiness
 */
const AssetHistoryLayerContainer = ({
  map,
  view,
  timeline,
  onSetView,
  queryParams,
  onLayerDrawn,
  hoveredEvent,
  initialExtent,
  onEventHovered,
  assetsRoutesHistory,
  isTripServicesEnabled
}: MapContainerProps) => {
  const isMounted = useHasMounted(null as any);
  const {
    dataProps,
    configProps,
    visionProps,
    uiStateProps,
    handlerProps,
    coreStateProps
  } = useAssetHistoryProvider();

  const {selectedTrip: selectedAsset} = dataProps;
  const {setIONVision, hasIOnVision}  = visionProps;
  const {state: dataState, boundary}  = coreStateProps;
  const {featureFilter}               = configProps;
  const isMoutedRef                   = useRef(isMounted);
  
  const {
    handleSelectedMarker : onTripHistoryMarkerSelect,
    toggleTimeSlider
  } = handlerProps;
  const {
    isPreferenceRefresh,
    isAssetSnapToRoadEnabled
  } = uiStateProps;

  const removeLayersFromMap = useCallback(() => {
    removeLayers(map,
      [
        IN_TRIP_NODE_LAYER,
        IN_TRIP_PATH_LAYER,
        BACKGROUND_NODE_LAYER,
        BACKGROUND_PATH_LAYER,
        BACKGROUND_WORLD_LAYER,
        END_EVENT_NODE_LAYER
      ]
    );
  }, [map]);

  useEffect(() => {
    if (view) {
      onSetView?.(view);
    }
  }, [view]);
  
  useEffect(() => {
    removeLayersFromMap();
  }, []);

  useEffect(() => {
    isMoutedRef.current = isMounted;
  }, [isMounted]);

  useEffect(() => {
    if (view && initialExtent) {
      view.when(() => {
        reactiveUtils.whenOnce(
          () => view.ready
        ).then(() => {
          const delay = setTimeout(() => {
            clearTimeout(delay);
            view.goTo(initialExtent, mapViewGoToOptions);
          }, 300);
        });
      });
    }
  }, [view, initialExtent]);

  useEffect(() => {
    if (isMoutedRef.current && queryParams) {
      removeLayersFromMap();
    }
  }, [safeJSONStringify(queryParams)]);
  
  const layerProps = useMemo(() => ({
    hoveredEvent,
    featureFilter,
    selectedAsset,
    onEventHovered,
    assetsRoutesHistory,
    isPreferenceRefresh,
    isAssetSnapToRoadEnabled
  }), [
    hoveredEvent,
    featureFilter,
    selectedAsset,
    onEventHovered,
    assetsRoutesHistory,
    isPreferenceRefresh,
    isAssetSnapToRoadEnabled
  ]);

  const boundaryProps = useMemo(() => ({
    boundary
  }), [boundary]);

  const commonProps = useMemo(() => ({
    layers             : [HISTORY_LAYER],
    isSelectionEnabled : true,
    isAssetHistory     : true,
    timeline,
    dataState,
    hasIOnVision,
    setIONVision,
    streetViewClick    : setStreetViewAsset,
    onLayerDrawn,
    toggleTimeSlider,
    isTripServicesEnabled,
    onTripHistoryMarkerSelect
  }), [
    timeline,
    dataState,
    hasIOnVision,
    setIONVision,
    onLayerDrawn,
    toggleTimeSlider,
    isTripServicesEnabled,
    onTripHistoryMarkerSelect
  ]);

  if (isMounted && map && view) {
    return (
      <AssetHistoryLayerV2 map={map}
        view={view}
        layerProps={layerProps}
        boundaryProps={boundaryProps}
        commonProps={commonProps}/>
    );
  }
};

export default AssetHistoryLayerContainer;
